import React, { RefObject, useMemo, useState } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import log from 'loglevel'
import styled, { css } from 'styled-components/macro'
import { SpaceProps } from 'styled-system'
import palette from 'src/designSystem/variables/palette'
import {
  UserRecord,
  UserState,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { SetAssigneesRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import { Identifier } from '@trustero/trustero-api-web/lib/common/model_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { SetPolicyAssigneesRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { RiskServicePromiseClient } from '@trustero/trustero-api-web/lib/risk/risk_grpc_web_pb'
import { SetRiskAssigneesRequest } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import { SetVendorAssigneesRequest } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { PERMISSIONS } from 'src/config/roleConfig'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import { useUsers } from 'src/components/async/Users/useUsers'
import {
  AssigneeDisplay,
  useAssigneeDisplayNames,
} from 'src/components/Reusable/AssigneeDisplay'
import { Gravatar } from 'src/components/Gravatar'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import { DROPDOWN_SEARCH_THRESHOLD } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import { IconButton, IconButtonVariant } from '../IconButton'
import { useAuthorizedGrpcClientWithContentUpdate } from '../../../../adapter/grpcClient'
import useClickOutside from '../../../../Utils/useClickOutside'

const AssigneeContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
`

const AssigneeToggle = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${palette.neutral[100]};
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      color: ${palette.neutral[400]};
    `}

  &:disabled {
    cursor: default;
  }
`

const AssigneeDropdownMenu = styled.ul<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  width: 320px;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`

const UserList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`

const AssigneeDropdownItem = styled.button`
  width: 100%;
  padding: 4px 12px;
  border: none;
  background: white;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }

  .form-check {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0;
  }

  .form-check-label {
    overflow: hidden;
    cursor: pointer;
  }
`

const UserLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`

const UserName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

type AssigneeButtonProps = SpaceProps & {
  modelId: string
  modelType: MODEL_TYPE
  assigneeEmails: string[]
  ownerEmail: string | null
  requiredPermissions?: PERMISSIONS[]
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  isDisabled?: boolean
  onUpdate?: () => void
}

export const AssigneeButton = ({
  modelId,
  modelType,
  assigneeEmails,
  // ownerEmail is consumed for permission context but not forwarded to DOM
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ownerEmail: _ownerEmail,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  isDisabled,
  onUpdate,
  ...spaceProps
}: AssigneeButtonProps): JSX.Element => {
  const hasPermission_ = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities_ = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const hasPermission = requiredCapabilities ? hasCapabilities_ : hasPermission_
  const response = useUsers()
  const [menuIsToggled, setMenuIsToggled] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useClickOutside(() => {
    setMenuIsToggled(false)
    setSearch('')
  }) as RefObject<HTMLDivElement>

  const modelClient =
    useAuthorizedGrpcClientWithContentUpdate(ModelPromiseClient)
  const policyClient =
    useAuthorizedGrpcClientWithContentUpdate(PolicyPromiseClient)
  const riskClient = useAuthorizedGrpcClientWithContentUpdate(
    RiskServicePromiseClient,
  )
  const vendorClient =
    useAuthorizedGrpcClientWithContentUpdate(VendorPromiseClient)

  const activeUsers = useMemo(() => {
    if (!response.data) return []
    return response.data
      .getUsersList()
      .map((u: UserRecord) => u.toObject())
      .filter((u: UserRecord.AsObject) => u.state === UserState.USER_ACTIVE)
      .sort((a: UserRecord.AsObject, b: UserRecord.AsObject) =>
        (a.email ?? '').localeCompare(b.email ?? ''),
      )
  }, [response.data])

  const filteredUsers = useMemo(() => {
    if (!search) return activeUsers
    const q = search.toLowerCase()
    return activeUsers.filter(
      (u: UserRecord.AsObject) =>
        u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q),
    )
  }, [activeUsers, search])

  const selectedSet = useMemo(() => new Set(assigneeEmails), [assigneeEmails])

  const callSetAssignees = async (emails: string[]): Promise<void> => {
    try {
      switch (modelType) {
        case MODEL_TYPE.CONTROL: {
          const req = new SetAssigneesRequest()
            .setId(
              new Identifier()
                .setModelid(modelId)
                .setModeltype(MODEL_TYPE.CONTROL),
            )
            .setAssigneeEmailsList(emails)
          await modelClient.setAssignees(req)
          break
        }
        case MODEL_TYPE.POLICY: {
          const req = new SetPolicyAssigneesRequest()
            .setPolicyId(modelId)
            .setAssigneeEmailsList(emails)
          await policyClient.setAssignees(req)
          break
        }
        case MODEL_TYPE.RISK: {
          const req = new SetRiskAssigneesRequest()
            .setRiskId(modelId)
            .setAssigneeEmailsList(emails)
          await riskClient.setAssignees(req)
          break
        }
        case MODEL_TYPE.VENDOR: {
          const req = new SetVendorAssigneesRequest()
            .setVendorId(modelId)
            .setAssigneeEmailsList(emails)
          await vendorClient.setVendorAssignees(req)
          break
        }
        default:
          return
      }
      onUpdate?.()
    } catch (err) {
      log.error(`Error setting assignees for model ${modelId}`, err)
    }
  }

  const toggleAssignee = async (email: string): Promise<void> => {
    const next = new Set(selectedSet)
    if (next.has(email)) {
      next.delete(email)
    } else {
      next.add(email)
    }
    await callSetAssignees(Array.from(next))
  }

  const { displayName } = useAssigneeDisplayNames()

  const tooltipBody = assigneeEmails.length
    ? assigneeEmails.map(displayName).join(', ')
    : isDisabled || !hasPermission
    ? 'You do not have permission to manage assignees'
    : 'Add Assignees'

  return (
    <Tooltip
      id={`assignee-tooltip-${modelId}`}
      placement="top"
      tooltipBody={tooltipBody}
    >
      <AssigneeContainer ref={containerRef} {...spaceProps}>
        {assigneeEmails.length === 0 ? (
          <IconButton
            variant={IconButtonVariant.icon}
            disabled={isDisabled || !hasPermission}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMenuIsToggled((s) => !s)
              if (menuIsToggled) setSearch('')
            }}
          >
            <Gravatar email={null} />
          </IconButton>
        ) : (
          <AssigneeToggle
            type="button"
            $isOpen={menuIsToggled}
            disabled={isDisabled || !hasPermission}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMenuIsToggled((s) => !s)
              if (menuIsToggled) setSearch('')
            }}
          >
            <AssigneeDisplay emails={assigneeEmails} />
          </AssigneeToggle>
        )}
        <AssigneeDropdownMenu
          aria-expanded={menuIsToggled}
          role="list"
          isOpen={menuIsToggled}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {(activeUsers.length > DROPDOWN_SEARCH_THRESHOLD || !!search) && (
            <DropdownSearchInput
              value={search}
              onChange={setSearch}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              autoFocus={menuIsToggled}
            />
          )}
          <UserList>
            {filteredUsers.map((user: UserRecord.AsObject) => (
              <AssigneeDropdownItem
                key={user.email}
                type="button"
                title={
                  user.name ? `${user.name} (${user.email})` : user.email ?? ''
                }
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleAssignee(user.email ?? '')
                }}
              >
                <CheckBoxInput
                  id={`assignee-${modelId}-${user.email}`}
                  checked={selectedSet.has(user.email ?? '')}
                  onChange={() => toggleAssignee(user.email ?? '')}
                  onClick={(e) => e.stopPropagation()}
                  tabIndex={-1}
                  label={
                    <UserLabel>
                      <Gravatar email={user.email} />
                      <UserName>{user.name || user.email}</UserName>
                    </UserLabel>
                  }
                />
              </AssigneeDropdownItem>
            ))}
          </UserList>
        </AssigneeDropdownMenu>
      </AssigneeContainer>
    </Tooltip>
  )
}
